import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  StatDocumentVente,
  StatFacturesMois,
  StatEncaissements,
  StatNouveauxClients,
  StatArticlesActifs,
} from '../models/models';
import api from './api';

// ── State ────────────────────────────────────────────────────────────────────

interface DashboardState {
  ventesJour:        StatDocumentVente | null;
  encaissementsJour: StatEncaissements | null;
  commandesEnCours:  StatDocumentVente | null;
  facturesMois:      StatFacturesMois  | null;
  nouveauxClients:   StatNouveauxClients | null;
  articlesActifs:    StatArticlesActifs | null;
  loading: boolean;
  error:   string | null;
}

const initialState: DashboardState = {
  ventesJour:        null,
  encaissementsJour: null,
  commandesEnCours:  null,
  facturesMois:      null,
  nouveauxClients:   null,
  articlesActifs:    null,
  loading: false,
  error:   null,
};

// ── Thunks ───────────────────────────────────────────────────────────────────

export const fetchVentesJour = createAsyncThunk<
  StatDocumentVente, { departement_id?: number }, { rejectValue: string }
>('dashboard/ventesJour', async (params, thunkAPI) => {
  try {
    const res = await api.get('/dashboard/ventes-jour', { params });
    return res.data;
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e.response?.data?.message || 'Erreur ventes jour');
  }
});

export const fetchEncaissementsJour = createAsyncThunk<
  StatEncaissements, { departement_id?: number }, { rejectValue: string }
>('dashboard/encaissementsJour', async (params, thunkAPI) => {
  try {
    const res = await api.get('/dashboard/encaissements-jour', { params });
    return res.data;
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e.response?.data?.message || 'Erreur encaissements');
  }
});

export const fetchCommandesEnCours = createAsyncThunk<
  StatDocumentVente, { departement_id?: number }, { rejectValue: string }
>('dashboard/commandesEnCours', async (params, thunkAPI) => {
  try {
    const res = await api.get('/dashboard/commandes-en-cours', { params });
    return res.data;
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e.response?.data?.message || 'Erreur commandes');
  }
});

export const fetchFacturesMois = createAsyncThunk<
  StatFacturesMois, { departement_id?: number }, { rejectValue: string }
>('dashboard/facturesMois', async (params, thunkAPI) => {
  try {
    const res = await api.get('/dashboard/factures-mois', { params });
    return res.data;
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e.response?.data?.message || 'Erreur factures mois');
  }
});

export const fetchNouveauxClients = createAsyncThunk<
  StatNouveauxClients, void, { rejectValue: string }
>('dashboard/nouveauxClients', async (_, thunkAPI) => {
  try {
    const res = await api.get('/dashboard/nouveaux-clients');
    return res.data;
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e.response?.data?.message || 'Erreur nouveaux clients');
  }
});

export const fetchArticlesActifs = createAsyncThunk<
  StatArticlesActifs, { departement_id?: number }, { rejectValue: string }
>('dashboard/articlesActifs', async (params, thunkAPI) => {
  try {
    const res = await api.get('/dashboard/articles-actifs', { params });
    return res.data;
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e.response?.data?.message || 'Erreur articles actifs');
  }
});

// ── Slice ────────────────────────────────────────────────────────────────────

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardData: () => initialState,
  },
  extraReducers: (builder) => {
    const pending  = (state: DashboardState) => { state.loading = true; state.error = null; };
    const rejected = (state: DashboardState, action: any) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(fetchVentesJour.pending,        pending)
      .addCase(fetchVentesJour.rejected,       rejected)
      .addCase(fetchVentesJour.fulfilled,      (state, action) => { state.loading = false; state.ventesJour = action.payload; })

      .addCase(fetchEncaissementsJour.pending,   pending)
      .addCase(fetchEncaissementsJour.rejected,  rejected)
      .addCase(fetchEncaissementsJour.fulfilled, (state, action) => { state.loading = false; state.encaissementsJour = action.payload; })

      .addCase(fetchCommandesEnCours.pending,    pending)
      .addCase(fetchCommandesEnCours.rejected,   rejected)
      .addCase(fetchCommandesEnCours.fulfilled,  (state, action) => { state.loading = false; state.commandesEnCours = action.payload; })

      .addCase(fetchFacturesMois.pending,        pending)
      .addCase(fetchFacturesMois.rejected,       rejected)
      .addCase(fetchFacturesMois.fulfilled,      (state, action) => { state.loading = false; state.facturesMois = action.payload; })

      .addCase(fetchNouveauxClients.pending,     pending)
      .addCase(fetchNouveauxClients.rejected,    rejected)
      .addCase(fetchNouveauxClients.fulfilled,   (state, action) => { state.loading = false; state.nouveauxClients = action.payload; })

      .addCase(fetchArticlesActifs.pending,      pending)
      .addCase(fetchArticlesActifs.rejected,     rejected)
      .addCase(fetchArticlesActifs.fulfilled,    (state, action) => { state.loading = false; state.articlesActifs = action.payload; });
  },
});

export const { clearDashboardData } = dashboardSlice.actions;
export default dashboardSlice.reducer;
